import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { adminApi } from '../services/adminApi';
import { Loader2, Search, XCircle, ChevronUp, ChevronDown, Filter } from 'lucide-react';

type SortConfig = {
    key: 'email' | 'role' | 'ospStatus' | 'joined' | 'lastActiveAt';
    direction: 'asc' | 'desc';
};

export const AdminPage: React.FC = () => {
    const [loading, setLoading] = useState(true);
    const [users, setUsers] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [authorized, setAuthorized] = useState(false);

    // Filtering & Sorting State
    const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'joined', direction: 'desc' });
    const [filterRole, setFilterRole] = useState<string>('all');
    const [filterOsp, setFilterOsp] = useState<string>('all');

    const navigate = useNavigate();

    const fetchUsers = React.useCallback(async (query?: string) => {
        setLoading(true);
        try {
            const data = await adminApi.listUsers(query);
            setUsers(data.users || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        const checkAuth = async () => {
            try {
                await adminApi.whoami();
                setAuthorized(true);
                fetchUsers();
            } catch (err) {
                navigate('/'); // Redirect non-admins
            }
        };

        checkAuth();
    }, [navigate, fetchUsers]);

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        fetchUsers(search);
    };

    const requestSort = (key: SortConfig['key']) => {
        let direction: 'asc' | 'desc' = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedAndFilteredUsers = () => {
        let result = [...users];

        // 1. Filtering
        if (filterRole !== 'all') {
            result = result.filter(u => u.role === filterRole);
        }
        if (filterOsp !== 'all') {
            result = result.filter(u => u.ospStatus === filterOsp);
        }

        // 2. Sorting
        result.sort((a, b) => {
            let valA: any = a[sortConfig.key];
            let valB: any = b[sortConfig.key];

            // Handle dates
            if (sortConfig.key === 'joined' || sortConfig.key === 'lastActiveAt') {
                valA = valA ? new Date(valA).getTime() : 0;
                valB = valB ? new Date(valB).getTime() : 0;
            } else if (typeof valA === 'string') {
                valA = valA.toLowerCase();
                valB = valB?.toLowerCase() || '';
            }

            if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
            if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

        return result;
    };

    if (!authorized && loading) return <div className="p-10 text-center">Checking access...</div>;
    if (!authorized) return null;

    const filteredUsers = getSortedAndFilteredUsers();

    const SortIcon = ({ col }: { col: SortConfig['key'] }) => {
        if (sortConfig.key !== col) return <ChevronDown className="w-3 h-3 text-gray-300 ml-1 opacity-0 group-hover:opacity-100" />;
        return sortConfig.direction === 'asc' ? <ChevronUp className="w-3 h-3 text-blue-500 ml-1" /> : <ChevronDown className="w-3 h-3 text-blue-500 ml-1" />;
    };

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 p-8 font-sans">
            <div className="max-w-6xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gray-900 tracking-tight">Admin Dashboard</h1>
                        <p className="text-gray-500 mt-1">Manage users, roles and OSP entitlements</p>
                    </div>
                    <Link to="/" className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border rounded-lg hover:bg-gray-50 transition-all shadow-sm">
                        Back to App
                    </Link>
                </div>

                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    {/* Search */}
                    <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Search email or UID..."
                                className="w-full pl-10 pr-4 py-2 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none bg-white shadow-sm transition-all"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </div>
                        <button type="submit" className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors shadow-sm font-medium">
                            Search
                        </button>
                    </form>

                    {/* Filters */}
                    <div className="flex gap-2">
                        <div className="relative flex items-center bg-white border rounded-xl px-3 py-1 shadow-sm">
                            <Filter className="w-4 h-4 text-gray-400 mr-2" />
                            <select
                                className="bg-transparent text-sm font-medium outline-none pr-4"
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                            >
                                <option value="all">All Roles</option>
                                <option value="free">Free</option>
                                <option value="plus">Plus</option>
                                <option value="pro">Pro</option>
                            </select>
                        </div>
                        <div className="relative flex items-center bg-white border rounded-xl px-3 py-1 shadow-sm">
                            <Filter className="w-4 h-4 text-gray-400 mr-2" />
                            <select
                                className="bg-transparent text-sm font-medium outline-none pr-4"
                                value={filterOsp}
                                onChange={(e) => setFilterOsp(e.target.value)}
                            >
                                <option value="all">All OSP</option>
                                <option value="PRO">OSP Pro</option>
                                <option value="TRIAL_USED">Trial Used</option>
                                <option value="FREE">Free</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                    <table className="w-full text-left text-sm">
                        <thead className="bg-gray-50/50 border-b border-gray-200">
                            <tr>
                                <th onClick={() => requestSort('email')} className="p-4 font-semibold text-gray-500 cursor-pointer group hover:bg-gray-100/50 transition-colors">
                                    <div className="flex items-center">User <SortIcon col="email" /></div>
                                </th>
                                <th className="p-4 font-semibold text-gray-500">Role</th>
                                <th className="p-4 font-semibold text-gray-500">OSP Status</th>
                                <th onClick={() => requestSort('joined')} className="p-4 font-semibold text-gray-500 cursor-pointer group hover:bg-gray-100/50 transition-colors">
                                    <div className="flex items-center">Joined <SortIcon col="joined" /></div>
                                </th>
                                <th onClick={() => requestSort('lastActiveAt')} className="p-4 font-semibold text-gray-500 cursor-pointer group hover:bg-gray-100/50 transition-colors">
                                    <div className="flex items-center">Last Active <SortIcon col="lastActiveAt" /></div>
                                </th>
                                <th className="p-4 font-semibold text-gray-500 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-gray-400">
                                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 opacity-20" />
                                        <p className="font-medium">Fetching users...</p>
                                    </td>
                                </tr>
                            ) : filteredUsers.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="p-12 text-center text-gray-400">
                                        <p className="font-medium">No users found matching your filters.</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredUsers.map((user) => {
                                    const lastActive = user.lastActiveAt ? new Date(user.lastActiveAt) : null;
                                    const isRecent = lastActive && (new Date().getTime() - lastActive.getTime() < 5 * 60 * 1000); // 5 mins

                                    return (
                                        <tr key={user.uid} className="group hover:bg-gray-50/50 transition-colors">
                                            <td className="p-4">
                                                <div className="font-semibold text-gray-900">{user.email}</div>
                                                <div className="text-[11px] text-gray-400 font-mono mt-0.5">{user.uid}</div>
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider
                                                    ${user.role === 'pro' ? 'bg-purple-100 text-purple-700' :
                                                        user.role === 'plus' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>
                                                    {user.role}
                                                </span>
                                            </td>
                                            <td className="p-4">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider
                                                    ${user.ospStatus === 'PRO' ? 'bg-indigo-100 text-indigo-700' :
                                                        user.ospStatus === 'TRIAL_USED' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`}>
                                                    {user.trialAbuseBlocked && <XCircle className="w-3 h-3 mr-1 text-red-500" />}
                                                    {user.ospStatus}
                                                </span>
                                            </td>
                                            <td className="p-4 text-gray-500 font-medium">
                                                {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}
                                                <div className="text-[10px] opacity-60 font-normal">
                                                    {user.createdAt ? new Date(user.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {lastActive ? (
                                                    <div className="flex items-center gap-2">
                                                        {isRecent ? (
                                                            <span className="flex h-2 w-2 relative">
                                                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                                                                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                                                            </span>
                                                        ) : null}
                                                        <span className={isRecent ? 'text-green-700 font-bold' : 'text-gray-500'}>
                                                            {isRecent ? 'Online' : lastActive.toLocaleDateString()}
                                                        </span>
                                                        {!isRecent && <span className="text-[11px] text-gray-400">{lastActive.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>}
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">-</span>
                                                )}
                                            </td>
                                            <td className="p-4 text-right">
                                                <Link
                                                    to={`/admin/users/${user.uid}`}
                                                    className="inline-flex px-4 py-1.5 border border-gray-200 rounded-lg text-sm font-bold hover:bg-gray-900 hover:text-white hover:border-gray-900 transition-all text-gray-700"
                                                >
                                                    View Detail
                                                </Link>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
